'use client';

import { Button, enqueueSnackbar, Select } from '@bomdisoft/components';
import { getLanguageFromLocale, type SupportedLanguageCode, tryAgain } from '@bomdisoft/translations';
import s from '@pieleaf/app/[lang]/home/sections/hero/order-card.module.css';
import { publicGraphql } from '@pieleaf/database/pieleaf-api';
import { orderProduceText, productText, townText } from '@pieleaf/translations';
import { useParams, useRouter } from 'next/navigation';
import { type ChangeEvent, useEffect, useState } from 'react';

export const HeroOrderCard = () => {
  const params = useParams();
  const router = useRouter();
  const lang = typeof params.lang === 'string' ? params.lang : 'en';
  const languageCode = lang as SupportedLanguageCode;
  const language = getLanguageFromLocale(lang);

  const [products, setProducts] = useState<Product[]>([]);
  const [towns, setTowns] = useState<Town[]>([]);
  const [productId, setProductId] = useState('');
  const [townId, setTownId] = useState('');

  useEffect(() => {
    // The catalogue call must not be able to reject unhandled: a floating
    // promise here takes React's hydration down with it, so an unreachable API
    // turns the whole home page inert rather than just emptying two selects.
    const load = async () => {
      try {
        const { products: productData, towns: townData } = await publicGraphql<{ products: Product[]; towns: Town[] }>('{ products { id name unitName unitPrice } towns { id name agencies fee } }');
        setProducts((productData ?? []) as Product[]);
        setTowns((townData ?? []) as Town[]);
      } catch {
        // The selects stay empty and the CTA still routes to /orders, which
        // fetches again — so the visitor is not stranded on a dead hero.
        enqueueSnackbar(tryAgain[language], { variant: 'error' });
      }
    };

    load();
  }, [language]);

  const handleProductChange = (event: ChangeEvent<HTMLSelectElement>) => setProductId(event.target.value);
  const handleTownChange = (event: ChangeEvent<HTMLSelectElement>) => setTownId(event.target.value);

  const handleContinue = () => {
    const query = new URLSearchParams();
    if (productId) query.set('product', productId);
    if (townId) query.set('town', townId);
    const suffix = query.toString();
    router.push(`/${lang}/orders${suffix ? `?${suffix}` : ''}`);
  };

  return (
    <div className={s.card} data-testid='hero-order-card'>
      <Select aria-label={productText[language]} className={s.field} id='hero-product' label={productText[language]} name='heroProduct' onChange={handleProductChange} value={productId}>
        <option value=''>…</option>
        {products.map(candidate => (
          <option key={candidate.id} value={candidate.id}>
            {candidate.name[languageCode]}
          </option>
        ))}
      </Select>

      <Select aria-label={townText[language]} className={s.field} id='hero-town' label={townText[language]} name='heroTown' onChange={handleTownChange} value={townId}>
        <option value=''>…</option>
        {towns.map(candidate => (
          <option key={candidate.id} value={candidate.id}>
            {candidate.name}
          </option>
        ))}
      </Select>

      <Button aria-label={orderProduceText[language]} className={s.submit} data-prodantix='Continue to order produce' data-prodantix-action='place_order' data-testid='hero-order-continue' onClick={handleContinue} variant='contained'>
        {orderProduceText[language]}
      </Button>
    </div>
  );
};
